/**
 * pages/MobilPage.js
 * Lists the canonical cars collection with per-car item counts and management actions
 * (merge, rename, delete). Each row is clickable to open MobilDetailModal.
 * deriveCars is kept exported — App.js and MobilDetailModal still use it.
 */
import React, { useMemo, useState } from "react";
import { parseFitment } from "../utils/fitment";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { MergeIcon, PencilIcon, TrashIcon } from "../components/Icons";
import MobilManageModal from "../components/MobilManageModal";
import MobilAddModal from "../components/MobilAddModal";

/** Derive a sorted car list from the items array. Items with empty fitment are skipped. */
export function deriveCars(items) {
  const map = new Map();
  for (const it of items) {
    const names = parseFitment(it.fitment);
    for (const name of names) {
      const key = name.toLowerCase();
      if (!map.has(key)) map.set(key, { key, name, items: [] });
      map.get(key).items.push(it);
    }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export default function MobilPage({ cars = [], items = [], onOpenCar, onMergeCar, onRenameCar, onDeleteCar, onCreateCar }) {
  const [q, setQ] = useState("");
  const [addModal, setAddModal] = useState(false);
  const [manageModal, setManageModal] = useState(null); // { mode: "merge"|"rename", carName }
  const [deleteTarget, setDeleteTarget] = useState(null); // car name string

  // Build a name.toLowerCase() -> item count map from actual fitment data.
  const countMap = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      for (const f of parseFitment(it.fitment)) {
        const key = f.toLowerCase();
        m.set(key, (m.get(key) || 0) + 1);
      }
    }
    return m;
  }, [items]);

  const sorted = useMemo(
    () => [...cars].sort((a, b) => a.name.localeCompare(b.name)),
    [cars]
  );

  const visible = useMemo(() => {
    const low = q.trim().toLowerCase();
    return low ? sorted.filter((c) => c.name.toLowerCase().includes(low)) : sorted;
  }, [sorted, q]);

  const getCount = (name) => countMap.get(name.toLowerCase()) || 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Mobil</h1>
          <div className="page-sub">{cars.length} mobil</div>
        </div>
        <button className="btn btn-primary" onClick={() => setAddModal(true)}>+ Tambah Mobil</button>
      </div>

      <div className="toolbar">
        <input
          className="form-input grow"
          placeholder="Cari mobil…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Mobil</th>
              <th>Jumlah Barang</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={3} className="empty-state">Tidak ada mobil.</td></tr>
            ) : (
              visible.map((car) => (
                <tr key={car.name} style={{ cursor: "pointer" }} onClick={() => onOpenCar(car.name)}>
                  <td>{car.name}</td>
                  <td>{getCount(car.name)}</td>
                  <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="btn btn-sm"
                      onClick={() => setManageModal({ mode: "merge", carName: car.name })}><MergeIcon /> Gabung</button>
                    <button className="btn btn-sm"
                      onClick={() => setManageModal({ mode: "rename", carName: car.name })}><PencilIcon /> Ubah nama</button>
                    <button className="btn btn-sm btn-danger-ghost"
                      onClick={() => setDeleteTarget(car.name)}><TrashIcon /> Hapus</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {addModal && (
        <MobilAddModal
          cars={cars}
          onSubmit={(name) => { onCreateCar(name); setAddModal(false); }}
          onClose={() => setAddModal(false)}
        />
      )}

      {manageModal && (
        <MobilManageModal
          mode={manageModal.mode}
          car={manageModal.carName}
          cars={cars}
          onSubmit={(val) => {
            if (manageModal.mode === "merge") onMergeCar(manageModal.carName, val);
            else onRenameCar(manageModal.carName, val);
            setManageModal(null);
          }}
          onClose={() => setManageModal(null)}
        />
      )}

      {deleteTarget !== null && (
        <DeleteConfirmModal
          title="Hapus mobil?"
          message={<>Anda akan menghapus mobil "<b>{deleteTarget}</b>". Ini akan menghapusnya dari <b>{getCount(deleteTarget)}</b> barang. Tindakan ini permanen.</>}
          requireText="HAPUS"
          onConfirm={() => { onDeleteCar(deleteTarget); setDeleteTarget(null); }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
